import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { useDealVolumeSource } from 'hooks/usePartnerAdmin'
import { setVolumeSource } from 'submission/lib/partner-api'
import { formatDate } from 'views/Partners/format'
import { useResyncingDraft } from 'views/Partners/useResyncingDraft'
import type {
  VolumeSource,
  VolumeSourceBody,
  VolumeSourceKind,
} from 'views/Partners/types'

type Props = { dealId: string }

const KINDS: VolumeSourceKind[] = ['saleshub', 'allocation_commit', 'none']

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

type Draft = {
  kind: VolumeSourceKind
  chain_id: string
  contract_address: string
  track_ids: string
  token_decimals: string
  start_block: string
  enabled: boolean
  overridden: boolean
}

const toDraft = (v: VolumeSource): Draft => ({
  kind: v.kind,
  chain_id: String(v.chain_id),
  contract_address: v.contract_address,
  track_ids: v.track_ids.join(','),
  token_decimals: String(v.token_decimals),
  start_block: String(v.start_block),
  enabled: v.enabled,
  overridden: v.overridden,
})

const parseIds = (s: string): number[] =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '')
    .map(Number)

// Fields whose edit should automatically pin the source (tick Override), so
// an unsaved-looking edit isn't silently discarded by the next sync. This
// intentionally excludes `overridden` itself.
const VALUE_KEYS: (keyof Draft)[] = [
  'kind',
  'chain_id',
  'contract_address',
  'track_ids',
  'token_decimals',
  'start_block',
  'enabled',
]

const VolumeSourcePanel: React.FC<Props> = ({ dealId }) => {
  const { showToast } = useToast()
  const { data, error, isLoading, mutate } = useDealVolumeSource(dealId)
  const [draft, setDraft] = useResyncingDraft(data, toDraft)
  const [saving, setSaving] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  // Applies a value-field patch and, if it actually changes a value (not
  // `overridden` itself), also ticks Override in the same update — an admin
  // editing a derived source and saving without ticking Override would
  // otherwise get a success toast, then have the edit discarded by the next
  // backend sync.
  const patchDraft = (patch: Partial<Draft>) =>
    setDraft((d) => {
      if (!d) return d
      const next = { ...d, ...patch }
      const changesValue = VALUE_KEYS.some((k) => next[k] !== d[k])
      return changesValue ? { ...next, overridden: true } : next
    })

  const validate = (d: Draft): string | null => {
    const ids = parseIds(d.track_ids)
    if (ids.some((n) => !Number.isInteger(n) || n < 0))
      return 'Track IDs must be comma-separated non-negative integers'
    if (
      d.kind === 'allocation_commit' &&
      (!d.contract_address.trim() || Number(d.chain_id) === 0)
    )
      return 'allocation_commit needs a contract address and a chain ID'
    if (!Number.isInteger(Number(d.start_block)) || Number(d.start_block) < 0)
      return 'Start block must be a non-negative integer'
    if (
      !Number.isInteger(Number(d.token_decimals)) ||
      Number(d.token_decimals) <= 0
    )
      return 'Token decimals must be a positive integer'
    return null
  }

  const save = async () => {
    if (!draft) return
    const err = validate(draft)
    setLocalError(err)
    if (err) return
    const body: VolumeSourceBody = {
      kind: draft.kind,
      chain_id: Number(draft.chain_id),
      contract_address: draft.contract_address.trim(),
      track_ids: parseIds(draft.track_ids),
      token_decimals: Number(draft.token_decimals),
      start_block: Number(draft.start_block),
      enabled: draft.enabled,
      overridden: draft.overridden,
    }
    setSaving(true)
    try {
      await setVolumeSource(dealId, body)
      showToast({ type: 'success', message: 'Volume source saved' })
      mutate()
    } catch (e) {
      showToast({ type: 'error', message: (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="border border-neutral-200 rounded-lg p-4 space-y-3">
      <h2 className="font-semibold">Volume source</h2>
      {error && (
        <div className="text-sm text-red-600">
          Failed to load: {(error as Error).message}
        </div>
      )}
      {isLoading && <div className="text-sm text-neutral-600">Loading…</div>}
      {data && draft && (
        <>
          <div className="text-xs text-neutral-500">
            {data.overridden
              ? `Overridden by admin · updated ${formatDate(data.updated_at)}`
              : 'Derived from deal metadata. Tick "Override" to pin these values; untick to let the next sync re-derive.'}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-sm">
              Kind
              <select
                className={inputCls}
                value={draft.kind}
                onChange={(e) =>
                  patchDraft({ kind: e.target.value as VolumeSourceKind })
                }
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Chain ID
              <input
                className={inputCls}
                type="number"
                value={draft.chain_id}
                onChange={(e) => patchDraft({ chain_id: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              Contract address
              <input
                className={inputCls}
                value={draft.contract_address}
                onChange={(e) =>
                  patchDraft({ contract_address: e.target.value })
                }
              />
            </label>
            <label className="block text-sm">
              Track IDs (comma-separated)
              <input
                className={inputCls}
                value={draft.track_ids}
                onChange={(e) => patchDraft({ track_ids: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              Token decimals
              <input
                className={inputCls}
                type="number"
                value={draft.token_decimals}
                onChange={(e) => patchDraft({ token_decimals: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              Start block
              <input
                className={inputCls}
                type="number"
                value={draft.start_block}
                onChange={(e) => patchDraft({ start_block: e.target.value })}
              />
              <span className="text-xs text-neutral-500">
                First block the sync scans for allocation_commit sources. Set
                before enabling on a live chain; 0 scans from genesis.
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => patchDraft({ enabled: e.target.checked })}
              />
              Enabled
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.overridden}
                onChange={(e) =>
                  setDraft((d) => d && { ...d, overridden: e.target.checked })
                }
              />
              Override
            </label>
          </div>
          <div className="text-xs text-neutral-500">
            Editing any value above automatically ticks Override, pinning these
            values so the next sync won&apos;t discard them. Untick it to let
            the next sync re-derive this source.
          </div>
          {localError && (
            <div className="text-sm text-red-600">{localError}</div>
          )}
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>
              Save volume source
            </Button>
          </div>
        </>
      )}
    </section>
  )
}

export default VolumeSourcePanel
